import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  sendEmailVerification,
  updateProfile,
  onAuthStateChanged,
  sendPasswordResetEmail,
  confirmPasswordReset,
  verifyPasswordResetCode
} from 'firebase/auth';
import { 
  doc, 
  setDoc, 
  getDoc, 
  updateDoc, 
  serverTimestamp, 
  enableNetwork,
  collection,
  query,
  where,
  onSnapshot
} from 'firebase/firestore';
import { ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import { auth, db, storage } from '../firebase/config';
import { createChatWithSeller } from '../services/chatService'; // Import chat service
import { toast } from 'react-hot-toast'; // Import toast for notifications

const AuthContext = createContext({});

const ADMIN_CREDENTIALS = {
  email: 'admin@crossdeal.com',
  password: 'Admin@123',
  role: 'admin',
  name: 'System Administrator'
};

export const useAuth = () => useContext(AuthContext);

// Helper function to get roleId based on role
const getRoleId = (role) => {
  switch(role?.toLowerCase()) {
    case 'buyer': return 1;
    case 'seller': return 2;
    case 'admin': return 3;
    default: return 1;
  }
};

// Create user data structure
const createUserData = async (userData, role, authUser) => {
  const roleId = getRoleId(role);

  // Build user data structure
  const userDataStructure = {
    uid: authUser.uid,
    name: userData.name || '',
    email: authUser.email,
    displayName: userData.name || '',
    phone: userData.phone || '',
    countryCode: userData.countryCode || '+1',
    country: userData.country || '',
    state: userData.state || '',
    city: userData.city || '',
    postalCode: userData.postalCode || '',
    address: userData.address || '',
    
    // CRITICAL: Role assignment
    role: role,
    roleId: roleId,
    
    // Seller specific fields
    ...(role === 'seller' && {
      companyName: userData.companyName || '',
      companyDescription: userData.companyDescription || '',
      businessType: userData.businessType || '',
      GSTNumber: userData.GSTNumber || '',
      licenseNumber: userData.licenseNumber || '',
      companyLogo: userData.companyLogo ? 'uploaded' : null,
      licenseImage: userData.licenseImage ? 'uploaded' : null,
      port: userData.port || '',
      loadingPort: userData.loadingPort || '',
      destinationPort: userData.destinationPort || ''
    }),
    
    // Verification and status
    isEmailVerified: authUser.emailVerified || false,
    isMobileVerified: false,
    isApproved: false,
    isVerified: false,
    verificationStatus: 'pending',
    status: 'active',
    profilePercentage: calculateInitialProfilePercentage(userData, role),
    rating: 0,
    totalTransactions: 0,
    remarks: '',
    
    // Timestamps
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
    lastLoginAt: serverTimestamp(),
    emailVerifiedAt: null,
    
    // Preferences
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC',
    notificationsEnabled: true,
    marketingEmails: false,
    language: 'en',
    currency: 'USD'
  };

  return userDataStructure;
};

const calculateInitialProfilePercentage = (userData, role) => {
  const baseFields = ['name', 'email', 'phone', 'country', 'city', 'address'];
  const sellerFields = ['companyName', 'companyDescription', 'businessType'];
  
  const fields = role === 'seller' ? [...baseFields, ...sellerFields] : baseFields;
  const filledFields = fields.filter(field => {
    const value = userData[field];
    return value && value.toString().trim() !== '';
  }).length;
  
  return Math.round((filledFields / fields.length) * 100);
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [processingAuth, setProcessingAuth] = useState(false);
  const [authInitialized, setAuthInitialized] = useState(false);
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [unreadChatCount, setUnreadChatCount] = useState(0); // Add unread chat count state

  // Check network status
  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Track unread chat messages in real-time
  useEffect(() => {
    if (!user || !isOnline) {
      setUnreadChatCount(0);
      return;
    }

    const trackUnreadChats = () => {
      try {
        const chatsRef = collection(db, 'chats');
        const q = query(
          chatsRef,
          where('participants', 'array-contains', user.uid),
          where('status', '==', 'active')
        );

        const unsubscribe = onSnapshot(q, (snapshot) => {
          let totalUnread = 0;

          snapshot.forEach((doc) => {
            const chatData = doc.data();
            if (chatData.buyerId === user.uid) {
              totalUnread += chatData.buyerUnread || 0;
            } else if (chatData.sellerId === user.uid) {
              totalUnread += chatData.sellerUnread || 0;
            }
          });

          setUnreadChatCount(totalUnread);
        }, (error) => {
          console.error('Error tracking unread chats:', error);
          setUnreadChatCount(0);
        });

        return unsubscribe;
      } catch (error) {
        console.error('Error setting up unread chat tracker:', error);
        return () => {};
      }
    };

    const unsubscribe = trackUnreadChats();
    return () => {
      if (unsubscribe) unsubscribe();
    };
  }, [user, isOnline]);

  const adminLogin = async (email, password) => {
    try {
      setProcessingAuth(true);
      
      // Check if it's the hardcoded admin
      if (email === ADMIN_CREDENTIALS.email && password === ADMIN_CREDENTIALS.password) {
        // Create a mock Firebase user for admin
        const mockAdminUser = {
          uid: 'admin-uid-12345',
          email: ADMIN_CREDENTIALS.email,
          emailVerified: true,
          displayName: ADMIN_CREDENTIALS.name
        };
        
        // Create admin user data structure
        const adminData = {
          uid: mockAdminUser.uid,
          name: ADMIN_CREDENTIALS.name,
          email: ADMIN_CREDENTIALS.email,
          displayName: ADMIN_CREDENTIALS.name,
          phone: '',
          countryCode: '+1',
          country: '',
          state: '',
          city: '',
          postalCode: '',
          address: '',
          
          // Admin role assignment
          role: 'admin',
          roleId: 3,
          
          // Verification and status
          isEmailVerified: true,
          isMobileVerified: false,
          isApproved: true,
          isVerified: true,
          verificationStatus: 'approved',
          status: 'active',
          profilePercentage: 100,
          rating: 5,
          totalTransactions: 0,
          remarks: 'System Administrator',
          
          // Timestamps
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
          lastLoginAt: serverTimestamp(),
          emailVerifiedAt: serverTimestamp(),
          
          // Preferences
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC',
          notificationsEnabled: true,
          marketingEmails: false,
          language: 'en',
          currency: 'USD',
          
          // Admin specific flags
          isAdmin: true,
          permissions: ['all'],
          adminLevel: 'super'
        };
        
        // Check if admin document exists in Firestore
        try {
          const adminDoc = await getDoc(doc(db, 'users', mockAdminUser.uid));
          
          if (!adminDoc.exists()) {
            // Create admin user in Firestore
            await setDoc(doc(db, 'users', mockAdminUser.uid), adminData);
          } else {
            // Update last login time
            await updateDoc(doc(db, 'users', mockAdminUser.uid), {
              lastLoginAt: serverTimestamp(),
              updatedAt: serverTimestamp()
            });
            
            // Merge with existing data
            const existingData = adminDoc.data();
            adminData.createdAt = existingData.createdAt || serverTimestamp();
          }
        } catch (error) {
          console.warn('Could not save admin to Firestore:', error);
        }
        
        // Set the user state
        setUser(adminData);
        setProcessingAuth(false);
        
        return {
          success: true,
          user: adminData,
          message: 'Admin login successful'
        };
      }
      
      const result = await login(email, password);
      
      // Check if logged in user is an admin from Firestore
      if (result.success && result.user?.role === 'admin') {
        return result;
      } else if (result.success) {
        // If user is not admin, log them out
        await logout();
        return {
          success: false,
          message: 'Access denied. Admin privileges required.'
        };
      }
      
      return result;
      
    } catch (error) {
      console.error('Admin login error:', error);
      setProcessingAuth(false);
      return {
        success: false,
        message: error.message || 'Admin login failed'
      };
    }
  };

  const modifiedLogin = async (email, password) => {
    // Check if it's admin email
    if (email === ADMIN_CREDENTIALS.email) {
      return await adminLogin(email, password);
    }
    
    // Normal user login
    return await login(email, password);
  };

 const fetchUserData = useCallback(async (firebaseUser, retryCount = 0) => {
  try {
    if (!firebaseUser) return null;
    
    console.log('🔄 fetchUserData - START for:', firebaseUser.email);
    
    // Add cache-busting timestamp to prevent stale reads
    const cacheBuster = Date.now();
    
    let userData = null;
    let firestoreData = null;
    
    // First, try to get the user document with specific field selection
    try {
      console.log(`📡 Attempting to fetch from Firestore (attempt ${retryCount + 1})...`);
      
      // Use getDoc to get the document
      const userDoc = await getDoc(doc(db, 'users', firebaseUser.uid));
      
      if (userDoc.exists()) {
        firestoreData = userDoc.data();
        
        console.log('✅ Firestore document retrieved:', {
          uid: firestoreData.uid,
          role: firestoreData.role,
          roleId: firestoreData.roleId,
          email: firestoreData.email,
          hasRoleField: 'role' in firestoreData,
          timestamp: new Date().toISOString(),
          cacheBuster
        });
        
        // CRITICAL: Check if role exists in data
        if (!firestoreData.role) {
          console.warn('⚠️ WARNING: No role field in Firestore document!');
          console.log('Full document data:', firestoreData);
        }
        
      } else {
        console.warn('⚠️ No document found in Firestore for UID:', firebaseUser.uid);
      }
    } catch (firestoreError) {
      console.error('❌ Firestore read error:', firestoreError);
      
      // If we're offline or there's a network error, use minimal data
      if (!isOnline || firestoreError.code === 'unavailable') {
        console.log('📴 Offline mode - using minimal user data');
        return {
          uid: firebaseUser.uid,
          email: firebaseUser.email,
          emailVerified: firebaseUser.emailVerified,
          displayName: firebaseUser.displayName || '',
          name: firebaseUser.displayName || '',
          role: 'buyer',
          roleId: 1,
          isOffline: true
        };
      }
      
      // For other errors, retry if we haven't exceeded retry count
      if (retryCount < 2) {
        console.log(`🔄 Retrying fetch (${retryCount + 1}/2)...`);
        await new Promise(resolve => setTimeout(resolve, 1000 * (retryCount + 1)));
        return fetchUserData(firebaseUser, retryCount + 1);
      }
      
      throw firestoreError;
    }
    
    // If we have Firestore data, use it
    if (firestoreData) {
      // IMPORTANT: Create a NEW object with Firestore data as base
      userData = {
        // Start with ALL Firestore data
        ...firestoreData,
        
        // Ensure these fields are correct
        uid: firebaseUser.uid,
        email: firebaseUser.email,
        emailVerified: firebaseUser.emailVerified,
        
        // Make sure name fields are set
        displayName: firestoreData.displayName || firebaseUser.displayName || '',
        name: firestoreData.name || firestoreData.displayName || firebaseUser.displayName || '',
        
        // DOUBLE CHECK role - use Firestore's role or fallback
        role: firestoreData.role || 'buyer',
        roleId: firestoreData.roleId || getRoleId(firestoreData.role || 'buyer')
      };
      
      console.log('✅ Final user data (after Firestore merge):', {
        uid: userData.uid,
        role: userData.role,
        roleId: userData.roleId,
        source: 'firestore'
      });
      
      // Update last login time
      if (isOnline) {
        try {
          await updateDoc(doc(db, 'users', firebaseUser.uid), {
            lastLoginAt: serverTimestamp(),
            updatedAt: serverTimestamp()
          });
        } catch (updateError) {
          console.warn('Could not update last login:', updateError);
        }
      }
      
    } else {
      // No Firestore data found - create minimal/default
      console.log('📝 No Firestore data found, creating minimal user');
      userData = {
        uid: firebaseUser.uid,
        email: firebaseUser.email,
        emailVerified: firebaseUser.emailVerified,
        displayName: firebaseUser.displayName || '',
        name: firebaseUser.displayName || '',
        role: 'buyer',
        roleId: 1,
        profilePercentage: 0,
        status: 'active'
      };
    }
    
    console.log('✅ fetchUserData - COMPLETE, returning:', {
      uid: userData.uid,
      role: userData.role,
      roleId: userData.roleId
    });
    
    return userData;
    
  } catch (error) {
    console.error('❌ Error in fetchUserData:', error);
    
    // Ultimate fallback
    return {
      uid: firebaseUser.uid,
      email: firebaseUser.email,
      emailVerified: firebaseUser.emailVerified,
      displayName: firebaseUser.displayName || '',
      name: firebaseUser.displayName || '',
      role: 'buyer',
      roleId: 1,
      isOffline: true,
      hasError: true
    };
  }
}, [isOnline]);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      setProcessingAuth(true);
      
      if (firebaseUser) {
        try {
          const userData = await fetchUserData(firebaseUser);
          if (userData) {
            setUser(userData);
          } else {
            setUser(null);
          }
        } catch (error) {
          console.error('Error processing user:', error);
          setUser(null);
        }
      } else {
        setUser(null);
      }
      
      setLoading(false);
      setProcessingAuth(false);
      setAuthInitialized(true);
    }, (error) => {
      console.error('Auth state change error:', error);
      setUser(null);
      setLoading(false);
      setProcessingAuth(false);
      setAuthInitialized(true);
    });

    return unsubscribe;
  }, [fetchUserData]);

  const register = async (userData) => {
    try {
      setProcessingAuth(true);
      
      if (!isOnline) {
        throw new Error('No internet connection. Please check your network and try again.');
      }

      // Validate role
      if (!userData.role || (userData.role !== 'buyer' && userData.role !== 'seller')) {
        throw new Error('Invalid role specified. Must be "buyer" or "seller"');
      }
      
      // Create auth user
      const userCredential = await createUserWithEmailAndPassword(
        auth,
        userData.email,
        userData.password
      );

      const firebaseUser = userCredential.user;

      // Update profile with name
      if (userData.name) {
        await updateProfile(firebaseUser, {
          displayName: userData.name,
        });
      }

      const role = userData.role;
      
      // Create complete user data
      const completeUserData = await createUserData(userData, role, firebaseUser);
      
      // Save to Firestore
      await setDoc(doc(db, 'users', firebaseUser.uid), completeUserData);

      // Send email verification
      try {
        await sendEmailVerification(firebaseUser);
      } catch (emailError) {
        console.warn('Email verification failed:', emailError.message);
      }

      // Prepare final user data
      const finalUserData = {
        uid: firebaseUser.uid,
        email: firebaseUser.email,
        emailVerified: firebaseUser.emailVerified,
        displayName: userData.name,
        ...completeUserData
      };

      setUser(finalUserData);
      setProcessingAuth(false);
      
      return {
        success: true,
        user: finalUserData,
        message: 'Registration successful! Please check your email for verification.'
      };
    } catch (error) {
      console.error('Registration failed:', error);
      
      let message = 'Registration failed';
      switch (error.code) {
        case 'auth/email-already-in-use':
          message = 'Email already registered. Please login instead.';
          break;
        case 'auth/invalid-email':
          message = 'Invalid email address format.';
          break;
        case 'auth/weak-password':
          message = 'Password must be at least 6 characters.';
          break;
        case 'auth/operation-not-allowed':
          message = 'Email/password accounts are not enabled.';
          break;
        case 'auth/network-request-failed':
          message = 'Network error. Please check your internet connection.';
          break;
        case 'auth/too-many-requests':
          message = 'Too many requests. Please try again later.';
          break;
        default:
          message = error.message || 'Registration failed. Please try again.';
      }
      
      setProcessingAuth(false);
      return {
        success: false,
        message
      };
    }
  };

  const login = async (email, password) => {
    try {
      setProcessingAuth(true);
      
      if (!isOnline) {
        throw new Error('No internet connection. Please check your network and try again.');
      }

      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const firebaseUser = userCredential.user;
      
      const userData = await fetchUserData(firebaseUser);
      
      if (userData) {
        setUser(userData);
        setProcessingAuth(false);
        
        return {
          success: true,
          user: userData,
          message: 'Login successful'
        };
      } else {
        setProcessingAuth(false);
        return {
          success: false,
          message: 'Failed to load user profile'
        };
      }
      
    } catch (error) {
      console.error('Login error:', error);
      
      let message = 'Login failed';
      switch (error.code) {
        case 'auth/user-not-found':
          message = 'No account found with this email.';
          break;
        case 'auth/wrong-password':
          message = 'Incorrect password. Please try again.';
          break;
        case 'auth/invalid-credential':
          message = 'Invalid email or password.';
          break;
        case 'auth/user-disabled':
          message = 'This account has been disabled.';
          break;
        case 'auth/too-many-requests':
          message = 'Too many login attempts. Please try again later.';
          break;
        case 'auth/network-request-failed':
          message = 'Network error. Please check your connection.';
          break;
        default:
          message = error.message || 'Login failed. Please try again.';
      }
      
      setProcessingAuth(false);
      return {
        success: false,
        message
      };
    }
  };

  const logout = async () => {
    try {
      await signOut(auth);
      setUser(null);
      setUnreadChatCount(0);
      return { success: true };
    } catch (error) {
      console.error('Logout error:', error);
      return { success: false, message: error.message };
    }
  };

  const forgotPassword = async (email) => {
    try {
      if (!isOnline) {
        throw new Error('No internet connection. Please check your network.');
      }
      
      // Firebase sends reset email directly
      await sendPasswordResetEmail(auth, email);
      
      return { 
        success: true, 
        message: 'Password reset email sent! Check your inbox and follow the link to reset your password.' 
      };
    } catch (error) {
      console.error('Forgot password error:', error);
      
      let message = 'Failed to send reset email';
      switch (error.code) {
        case 'auth/user-not-found':
          message = 'No account found with this email address.';
          break;
        case 'auth/invalid-email':
          message = 'Invalid email address format.';
          break;
        case 'auth/too-many-requests':
          message = 'Too many requests. Please try again later.';
          break;
        case 'auth/network-request-failed':
          message = 'Network error. Please check your internet connection.';
          break;
        default:
          message = error.message || 'Failed to send reset email.';
      }
      
      return { success: false, message };
    }
  };

  // Simplified: Verify and reset password in one step
  const resetPasswordWithCode = async (code, newPassword) => {
    try {
      if (!isOnline) {
        throw new Error('No internet connection. Please check your network.');
      }
      
      // Validate password
      if (newPassword.length < 6) {
        throw new Error('Password must be at least 6 characters long.');
      }
      
      // Verify the reset code first
      const email = await verifyPasswordResetCode(auth, code);
      
      // Confirm password reset
      await confirmPasswordReset(auth, code, newPassword);
      
      return { 
        success: true, 
        message: 'Password reset successful! You can now login with your new password.',
        email: email
      };
    } catch (error) {
      console.error('Reset password error:', error);
      
      let message = 'Failed to reset password';
      switch (error.code) {
        case 'auth/expired-action-code':
          message = 'Reset link has expired. Please request a new one.';
          break;
        case 'auth/invalid-action-code':
          message = 'Invalid reset link. Please request a new one.';
          break;
        case 'auth/weak-password':
          message = 'Password is too weak. Please choose a stronger password.';
          break;
        case 'auth/too-many-requests':
          message = 'Too many attempts. Please try again later.';
          break;
        case 'auth/user-disabled':
          message = 'This account has been disabled.';
          break;
        case 'auth/user-not-found':
          message = 'No account found. Please sign up first.';
          break;
        default:
          message = error.message || 'Failed to reset password. Please try again.';
      }
      
      return { success: false, message };
    }
  };

  // Chat-related functions
  const startChatWithSeller = async (product, quantity = 1, customMessage = '') => {
    try {
      if (!user) {
        throw new Error('Please login to start a chat');
      }
      
      if (!product?.sellerId) {
        throw new Error('Cannot start chat: Seller information missing');
      }
      
      if (user.uid === product.sellerId) {
        throw new Error('You cannot chat with yourself');
      }
      
      if (user.role !== 'buyer') {
        throw new Error('Only buyers can start chats with sellers');
      }
      
      const buyerName = user.displayName || user.name || user.email?.split('@')[0] || 'Buyer';
      const buyerEmail = user.email;
      
      const chatData = {
        buyerId: user.uid,
        buyerName: buyerName,
        buyerEmail: buyerEmail,
        buyerPhoto: user.photoURL || '',
        
        sellerId: product.sellerId,
        sellerName: product.sellerName || product.companyName || 'Seller',
        sellerEmail: product.sellerEmail || product.sellerContactEmail || '',
        sellerVerified: product.sellerVerified || false,
        
        productId: product.id,
        productName: product.name,
        productImage: product.images?.[0]?.data || '',
        
        initialMessage: customMessage || `Hi, I'm interested in your product "${product.name}". Can you provide more details about:`,
        quantity: quantity,
        unit: product.unit || 'PCS',
        totalPrice: (product.price || 0) * quantity,
        moq: product.moq || 1,
        unitPrice: product.price || 0
      };
      
      const chatId = await createChatWithSeller(chatData);
      
      return { 
        success: true, 
        chatId, 
        message: 'Chat started successfully' 
      };
      
    } catch (error) {
      console.error('Error starting chat:', error);
      return { 
        success: false, 
        message: error.message || 'Failed to start chat' 
      };
    }
  };

  const canChatWithSeller = (product) => {
    if (!user) return false;
    if (!product?.sellerId) return false;
    
    // User must be buyer to chat with seller
    if (user.role !== 'buyer') return false;
    
    // Buyer cannot chat with themselves
    if (user.uid === product.sellerId) return false;
    
    return true;
  };

  const value = {
    // User state
    user,
    loading,
    authInitialized,
    processingAuth,
    isOnline,
    
    // Authentication methods
    login: modifiedLogin,
    adminLogin,
    register,
    logout,
    forgotPassword,
    resetPasswordWithCode,
    
    // User role checks
    isAuthenticated: !!user,
    isBuyer: user?.role === 'buyer',
    isSeller: user?.role === 'seller',
    isAdmin: user?.role === 'admin',
    
    // Chat functionality
    startChatWithSeller,
    canChatWithSeller,
    unreadChatCount, // Real-time unread message count
    
    // Helper methods
    getUserFullName: () => {
      if (!user) return '';
      return user.displayName || user.name || user.email?.split('@')[0] || 'User';
    },
    
    getUserInitials: () => {
      if (!user) return '';
      const name = user.displayName || user.name || '';
      if (!name) return user.email?.charAt(0).toUpperCase() || 'U';
      return name.split(' ').map(n => n[0]).join('').toUpperCase();
    }
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};